import { logV } from "../../services/debug-logger";
import type { ExcelGridCellProps } from "../models/excel-grid-component-props.models";
import { getCellTone, toCellText } from "../utils/excel-grid.utils";
import { getGridCellHeightClassName } from "../utils/excel-like-grid-ui.utils";
import { ExcelGridCellEditor } from "./ExcelGridCellEditor";
import { ExcelGridCellSelectEditor } from "./ExcelGridCellSelectEditor";
import { twMerge } from "tailwind-merge";

export const ExcelGridCell = ({
  row,
  column,
  rowHeight,
  rowIndex,
  columnIndex,
  dataTest,
  isEditing,
  isSelected,
  isSaving,
  editValue,
  selectedCellRef,
  onCancelEditing,
  onCommitEditing,
  onCommitEditingValue,
  onEditValueChange,
  onFocusGrid,
  onStartEditing,
  onSelectionChange,
  cellEditorMode,
  selectMenuPortalTarget,
}: ExcelGridCellProps) => {
  const cellTone = getCellTone(column, row[column.key]);
  const selection = { rowIndex, columnIndex };

  return (
    <td
      ref={(element) => {
        if (selectedCellRef) {
          selectedCellRef.current = element;
        }
      }}
      data-test={`${dataTest}-cell-${row.gridRowId}-${column.key}`}
      onClick={() => {
        if (isEditing) {
          return;
        }

        logV("ExcelGridCell - onClick", { rowIndex, column: column.key });
        onFocusGrid();
        onSelectionChange(selection);
      }}
      onDoubleClick={() => {
        if (isEditing) {
          return;
        }

        logV("ExcelGridCell - onDoubleClick", { rowIndex, column: column.key });
        onStartEditing(selection);
      }}
      className={twMerge(
        "border-greyscale-20 border-b border-r px-2 py-0.5 align-middle text-xs leading-4 outline-none dark:border-gray-700",
        "whitespace-pre-wrap break-words",
        getGridCellHeightClassName(rowHeight),
        column.editable === false && "bg-gray-50 text-gray-500 dark:bg-gray-900/60 dark:text-gray-400",
        cellTone === "yes" && "bg-[#EFFAF6] text-[#0F5F54] dark:bg-teal-950/60 dark:text-teal-100",
        cellTone === "no" && "bg-[#FFF1F1] text-[#8F0002] dark:bg-red-950/40 dark:text-red-100",
        isSelected && "relative bg-[#E9F4F3] shadow-[inset_0_0_0_2px_#1F8475] dark:bg-teal-900/40",
        isSaving && "relative pr-6 opacity-70",
      )}
    >
      {isEditing && column.editor?.type === "select" ? (
        <ExcelGridCellSelectEditor
          value={editValue}
          options={column.editor.options}
          isSearchable={column.editor.isSearchable}
          allowCustomValue={column.editor.allowCustomValue}
          placeholder={column.editor.placeholder}
          selectMenuPortalTarget={selectMenuPortalTarget}
          onCancel={onCancelEditing}
          onCommitValue={onCommitEditingValue}
        />
      ) : isEditing ? (
        <ExcelGridCellEditor
          value={editValue}
          cellEditorMode={cellEditorMode}
          onCancel={onCancelEditing}
          onCommit={onCommitEditing}
          onValueChange={onEditValueChange}
        />
      ) : column.renderCell ? (
        column.renderCell(row, column)
      ) : (
        toCellText(row[column.key])
      )}
      {isSaving && !isEditing ? (
        <span
          aria-label="Saving cell"
          className="absolute right-1 top-1 h-3 w-3 animate-spin rounded-full border-2 border-[#1F8475] border-t-transparent"
        />
      ) : null}
    </td>
  );
};
